import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAccount } from 'wagmi';
import { useAppKit } from '@reown/appkit/react';
import { socialLinks } from '../data/socialLinks';

const navLinks = [
  { path: '/credit', label: 'Credit', requireAuth: true },
  { path: '/affiliates', label: 'Affiliates', requireAuth: true },
  { path: '/games', label: 'Games' },
  { path: '/analytics', label: 'Analytics' },
  { path: '/history', label: 'History', requireAuth: true },
  { path: '/about', label: 'About' },
];

function NavHeader() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { address } = useAccount();
  const location = useLocation();
  const { open } = useAppKit();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  const visibleLinks = navLinks.filter((link) => !link.requireAuth || address);

  const socialButtonClass =
    'flex items-center justify-center w-10 h-10 rounded-full bg-dark-surface/80 border border-hash-gold/25 text-hash-gold hover:bg-hash-gold/15 hover:border-hash-gold/50 hover:scale-105 transition-all duration-300 shrink-0';

  const renderSocialLinks = (className = '') => (
    <div className={`flex items-center gap-2 ${className}`}>
      {socialLinks.map((social) => (
        <a
          key={social.name}
          href={social.href}
          target="_blank"
          rel="noopener noreferrer"
          className={socialButtonClass}
          title={`Join us on ${social.name}`}
          aria-label={`Join us on ${social.name}`}
        >
          {social.icon}
        </a>
      ))}
    </div>
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-dark-secondary/90 backdrop-blur-xl border-b border-hash-gold/10'
          : 'bg-transparent'
      }`}
    >
      <div className="container-premium">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="/images/logo.png"
              alt="HashMedora"
              className="h-10 w-auto object-contain"
            />
            <img
              src="/images/logo-text.png"
              alt="HashMedora"
              className="hidden sm:block h-8 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {visibleLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                  isActive(link.path)
                    ? 'text-hash-gold bg-hash-gold/10 border border-hash-gold/20'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}

            <div className="ml-3 pl-3 border-l border-white/10">{renderSocialLinks()}</div>

            {/* Wallet Button */}
            {address ? (
              <button
                onClick={() => open({ view: 'Account' })}
                className="ml-3 btn-gold px-4 py-2 text-sm"
              >
                {address.slice(0, 6)}...{address.slice(-4)}
              </button>
            ) : (
              <button
                onClick={() => open()}
                className="ml-3 btn-gold px-5 py-2 text-sm"
              >
                Connect Wallet
              </button>
            )}
          </nav>

          {/* Mobile: social + wallet + menu */}
          <div className="flex lg:hidden items-center gap-2.5">
            {renderSocialLinks()}
            {address && (
              <button
                onClick={() => open({ view: 'Account' })}
                className="px-3 py-1.5 rounded-lg bg-hash-gold/10 border border-hash-gold/20 text-hash-gold text-xs font-semibold"
              >
                {address.slice(0, 4)}...{address.slice(-2)}
              </button>
            )}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-lg text-hash-gold hover:bg-hash-gold/10 transition-colors"
            >
              {isMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="lg:hidden bg-dark-secondary/95 backdrop-blur-xl border-t border-hash-gold/10">
          <div className="container-premium py-4 space-y-1">
            {visibleLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-hash-gold bg-hash-gold/10'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="mt-3 pt-3 border-t border-white/5 px-4">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsMenuOpen(false)}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-lg border border-hash-gold/25 bg-hash-gold/5 text-hash-gold text-sm font-medium hover:bg-hash-gold/15 transition-colors"
                  aria-label={`Join us on ${social.name}`}
                >
                  {social.icon}
                  Join {social.name}
                </a>
              ))}
            </div>
            {!address && (
              <button
                onClick={() => {
                  setIsMenuOpen(false);
                  open();
                }}
                className="w-full mt-2 btn-gold px-4 py-3 text-sm"
              >
                Connect Wallet
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export default NavHeader;
